import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { GridColDef } from '@mui/x-data-grid/models';
import { Box, IconButton, Tooltip, Button, Badge } from '@mui/material';
import { Add, Search, Refresh, GetApp } from '@mui/icons-material';
import { MaterialMeanResponse, SearchMaterialMeansRequest, ACLAuthorizationRequest } from '@/api/bol';
import { parseJsonWithDates } from '@/utils/jsonUtils';
import { 
  PageLayout, 
  DataGridContainer, 
  JsonDetailPanel,
  SearchPanel,
  createMaterialMeanColumns,
  defaultMaterialMeanColumnVisibilityModel,
  MaterialMeanFormDialog,
  MaterialMeanDeleteDialog,
  GenericCSVExportDialog,
} from '@/components';
import { 
  useDataGridSelection, 
  usePagination, 
  useSnackbarNotification,
  useMaterialMeans,
  useStableRowId,
  usePermissions,
  useLogisticsObjectSearchUrl,
  getActionTooltip
} from '@/hooks';
import { exportToCSV, generateCSVFilename } from '@/utils/csvExport';
import { DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY } from '@/utils/searchDefaults';
import { getDefaultSavedQuery } from '@/utils/savedQueriesStorage';

const MaterialMeansPage: React.FC = () => {
  const { can } = usePermissions();
  const {
    getSearchQueryFromUrl,
    updateSearchQueryInUrl,
    copyShareableSearchLink,
  } = useLogisticsObjectSearchUrl({ queryParamName: 'search' });
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const initialSearchQuery = useMemo<SearchMaterialMeansRequest>(
    () => {
      const searchQueryFromUrl = getSearchQueryFromUrl() as SearchMaterialMeansRequest | undefined;

      return searchQueryFromUrl
        || getDefaultSavedQuery('material-mean')?.query as SearchMaterialMeansRequest
        || DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY;
    },
    [getSearchQueryFromUrl],
  );
  const [currentSearchQuery, setCurrentSearchQuery] = useState<SearchMaterialMeansRequest>(initialSearchQuery);

  const hasAppliedQueryOrSort = useMemo(() => {
    const hasQueryContent = !!currentSearchQuery?.query
      && typeof currentSearchQuery.query === 'object'
      && Object.keys(currentSearchQuery.query).length > 0;
    const hasSortContent = Array.isArray(currentSearchQuery?.sort)
      && currentSearchQuery.sort.length > 0;

    return hasQueryContent || hasSortContent;
  }, [currentSearchQuery]);
  
  const displayColumnsRef = useRef<{ columns: GridColDef[]; visibilityModel: Record<string, boolean> }>({
    columns: [],
    visibilityModel: defaultMaterialMeanColumnVisibilityModel,
  });

  const handleDisplayColumnsChange = useCallback((cols: GridColDef[], visibilityModel: Record<string, boolean>) => {
    displayColumnsRef.current = { columns: cols, visibilityModel };
  }, []);

  const getRowId = useStableRowId<MaterialMeanResponse>('material-mean');

  // Use custom hooks for material-mean management
  const {
    materialMeans,
    loading,
    selectedMaterialMean,
    setSelectedMaterialMean,
    fetchMaterialMeans,
    createMaterialMean,
    deleteMaterialMean,
    total,
  } = useMaterialMeans();

  // Use custom hooks for common functionality
  const { 
    selectedItem: selectedMaterialMeanForDetail, 
    selectedRowId,
    handleRowSelectionChange,
    handleCloseDetailPanel 
  } = useDataGridSelection<MaterialMeanResponse>({ 
    data: materialMeans, 
    getItemId: (item) => item.id || '' 
  });

  const {
    pagination,
    handlePaginationModelChange,
  } = usePagination({
    onPageChange: (page, pageSize) => {
      void fetchMaterialMeans(page, pageSize, currentSearchQuery);
    }
  });

  const {
    snackbarOpen,
    snackbarMessage,
    snackbarLink,
    snackbarType,
    showSuccess,
    showError,
    closeSnackbar
  } = useSnackbarNotification();

  // Event handlers
  const handleDeleteClick = (materialMean: MaterialMeanResponse) => {
    setSelectedMaterialMean(materialMean);
    setDeleteDialogOpen(true);
  };

  const handleRefresh = () => {
    fetchMaterialMeans(pagination.page, pagination.pageSize, currentSearchQuery);
  };

  const handleSearch = async (searchData: unknown) => {
    const searchBody = searchData as SearchMaterialMeansRequest;
    setCurrentSearchQuery(searchBody);
    const isSearchSuccessful = await fetchMaterialMeans(0, pagination.pageSize, searchBody); // Reset to first page on search

    if (isSearchSuccessful) {
      updateSearchQueryInUrl(searchBody);
    }
  };

  const handleSearchReset = async () => {
    const defaultSearchQuery = (getDefaultSavedQuery('material-mean')?.query as SearchMaterialMeansRequest)
      || DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY;
    setCurrentSearchQuery(defaultSearchQuery);
    const isResetSuccessful = await fetchMaterialMeans(0, pagination.pageSize, defaultSearchQuery); // Reset to first page with default search

    if (isResetSuccessful) {
      updateSearchQueryInUrl(defaultSearchQuery);
    }
  };

  const handleCopySearchLink = useCallback(async () => {
    await copyShareableSearchLink(currentSearchQuery, {
      onSuccess: showSuccess,
      onError: showError,
    });
  }, [copyShareableSearchLink, currentSearchQuery, showError, showSuccess]);

  const handleToggleSearchPanel = () => {
    setSearchPanelOpen(!searchPanelOpen);
  };

  const handleExportCSV = (separator: string) => {
    exportToCSV({
      data: materialMeans,
      columns: displayColumnsRef.current.columns,
      columnVisibilityModel: displayColumnsRef.current.visibilityModel,
      filename: generateCSVFilename('material_means'),
      separator,
    });
  };

  const handleCreateMaterialMean = async (data: { memberIdentifier: string; materialMeanJson: string; aclAuthorizations?: ACLAuthorizationRequest[] }) => {
    try {
      const { memberIdentifier, materialMeanJson, aclAuthorizations } = data;

      // Validate required fields
      if (!memberIdentifier?.trim()) {
        throw new Error('Member identifier is required');
      }
      
      if (!materialMeanJson?.trim()) {
        throw new Error('MaterialMean JSON is required');
      }

      // Parse and validate JSON
      let materialMeanData;
      try {
        materialMeanData = parseJsonWithDates(materialMeanJson);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        throw new Error('Invalid JSON format. Please check your JSON syntax.');
      }

      // Validate parsed data structure
      if (!materialMeanData || typeof materialMeanData !== 'object') {
        throw new Error('MaterialMean data must be a valid JSON object');
      }

      await createMaterialMean(materialMeanData, memberIdentifier, aclAuthorizations);
      setCreateDialogOpen(false);
      // Refresh the list after successful creation
      fetchMaterialMeans(pagination.page, pagination.pageSize, currentSearchQuery);
    } catch (error) {
      // Error is handled by the hook, but we can add additional client-side validation errors
      console.error('MaterialMean creation validation error:', error);
      throw error; // Re-throw to let the dialog handle the error display
    }
  };

  const handleDeleteMaterialMean = async () => {
    if (!selectedMaterialMean?.id) return;

    await deleteMaterialMean(selectedMaterialMean.id);
    setSelectedMaterialMean(null);
    // Refresh the list after successful deletion
    fetchMaterialMeans(pagination.page, pagination.pageSize, currentSearchQuery);
  };

  // Create columns configuration
  const columns = createMaterialMeanColumns({
    onDelete: handleDeleteClick,
    canDelete: can.deleteLogisticsObject,
    deleteTooltip: getActionTooltip('deleteLogisticsObject')
  });

  useEffect(() => {
    fetchMaterialMeans(0, 10, initialSearchQuery);
  }, [fetchMaterialMeans, initialSearchQuery]);

  return (
    <PageLayout 
      title="MaterialMeans Management"
      selectedItemInfo={selectedMaterialMeanForDetail ? `Selected: ${selectedMaterialMeanForDetail.id}` : undefined}
      notificationOpen={snackbarOpen}
      notificationMessage={snackbarMessage}
      notificationLink={snackbarLink}
      notificationType={snackbarType}
      onNotificationClose={closeSnackbar}
      leftTitleActions={
        <>
          <Tooltip title="Refresh material_means list">
            <IconButton
              onClick={handleRefresh}
              size="small"
            >
              <Refresh />
            </IconButton>
          </Tooltip>
          <Tooltip title={searchPanelOpen ? "Close search panel" : "Open search panel"}>
            <IconButton
              onClick={handleToggleSearchPanel}
              color={searchPanelOpen ? "primary" : "default"}
              size="small"
            >
              <Badge color="secondary" variant="dot" invisible={!hasAppliedQueryOrSort}>
                <Search />
              </Badge>
            </IconButton>
          </Tooltip>
        </>
      }
      titleActions={
          <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<GetApp />}
            onClick={() => setExportDialogOpen(true)}
            size="medium"
          >
            Export CSV
          </Button>
          <Tooltip title={!can.createLogisticsObject ? getActionTooltip('createLogisticsObject') : ''}>
          <span>
          <Button
                variant="contained"
                size="medium"
                onClick={() => setCreateDialogOpen(true)}
                startIcon={<Add />}
              disabled={!can.createLogisticsObject}
              >
                Create MaterialMean
              </Button>
          </span>
        </Tooltip>
        </Box>
      }
      leftPanel={searchPanelOpen ? (
        <SearchPanel
          title="Search MaterialMeans"
          onClose={() => setSearchPanelOpen(false)}
          onSearch={handleSearch}
          onReset={handleSearchReset}
          onCopyLink={handleCopySearchLink}
          loading={loading}
          logisticsObjectType="material-mean"
          currentSearchQuery={currentSearchQuery}
        />
      ) : undefined}
      sidePanel={selectedMaterialMeanForDetail ? (
        <JsonDetailPanel
          title="MaterialMean Details"
          data={selectedMaterialMeanForDetail}
          onClose={handleCloseDetailPanel}
          onCopyError={() => {}} // Error handling moved to hook
        />
      ) : undefined}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <DataGridContainer
          rows={materialMeans}
          columns={columns}
          loading={loading}
          selectedRowId={selectedRowId}
          onRowSelectionChange={handleRowSelectionChange}
          paginationModel={pagination}
          rowCount={total}
          onPaginationModelChange={handlePaginationModelChange}
          getRowId={getRowId}
          columnVisibilityModel={defaultMaterialMeanColumnVisibilityModel}
          columnOrderStorageKey="material_means-columns"
          onDisplayColumnsChange={handleDisplayColumnsChange}
        />
      </Box>

      {/* Create MaterialMean Dialog */}
      <MaterialMeanFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateMaterialMean}
      />

      {/* Delete Confirmation Dialog */}
      <MaterialMeanDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteMaterialMean}
        materialMean={selectedMaterialMean}
      />

      {/* CSV Export Dialog */}
      <GenericCSVExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onSubmit={handleExportCSV}
        title="Export MaterialMeans to CSV"
        entityName="material_means"
        defaultFilename={generateCSVFilename('material_means')}
        currentPageInfo={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalRows: total,
        }}
      />
    </PageLayout>
  );
};

export default MaterialMeansPage;